// A text someone sent into a conversation okthx is in. Pure (types only), and
// shared because the feed selectors need the same test the renderer uses: a
// received text stays where it arrived, and the thread shows it as a line.
import type { CommunicationReceivedArtifact } from "@/types/artifacts.ts";
import type { GetMessageResp } from "@/types/messages.ts";

/** A received text, as the thread shows it. */
export type InboundText = { sender: string; sentAt: Date; words: string };

/**
 * The card of a received text: a received communication from a phone number.
 * An email does not count. A subject, a recipient list and an HTML body do not
 * read as one line of a chat, so an email keeps its card.
 */
const receivedTextCard = (message: GetMessageResp): CommunicationReceivedArtifact | undefined => {
  if (message.type !== "externalCommunication") return undefined;
  const received = message.artifacts?.find(
    (a): a is CommunicationReceivedArtifact => a.artifactType === "communicationReceived",
  );
  return received?.from.type === "phoneNumber" ? received : undefined;
};

/**
 * Whether this message files a text someone sent. Such a text is part of the
 * conversation okthx answers, not a step okthx took, so it opens a turn.
 */
export const isReceivedText = (message: GetMessageResp) =>
  receivedTextCard(message) !== undefined;

/** The line to show for a received text, or nothing when it has no words. */
export const inboundTextOf = (message: GetMessageResp): InboundText | undefined => {
  const received = receivedTextCard(message);
  if (!received) return undefined;
  const words = (received.plainTextBody ?? received.body).trim();
  // A photo with no caption has nothing to read here; its card and the file
  // filed with it say what arrived.
  if (!words) return undefined;
  return {
    sender: received.from.name || received.from.value,
    sentAt: new Date(received.receivedAt),
    words,
  };
};
